import {
    inject,
    Injectable
} from '@angular/core';

import {
    EntityManagerService
} from 'src/app/core';

import {
    GenericListDialogService,
    WaitDialogService
} from 'src/app/dialogs';

import {
    readJsonOrThrow
} from 'src/app/shared/net/fetch-response';

import {
    DigitalPaymentHandler,
    DigitalPaymentHandlerOptions,
    DigitalPaymentHandlerResult
} from 'src/app/shared/model/digital-payments.model';

import {
    MathUtils
} from 'src/app/shared/utils';

import {
    ZPayBridge
} from './z-pay-bridge.model';

import {
    SalesPayments
} from 'tilby-models';

import {
    SaleUtilsService
} from 'src/app/features/cashregister';

import {
    TranslateService
} from '@ngx-translate/core';

const availableMethods: Set<ZPayBridge.SupportedMethods> = new Set([
    'auto',
    'debit',
    'credit',
    'ticket',
    'satispay',
]);

@Injectable({
    providedIn: 'root'
})
export class ZPayBridgeService implements DigitalPaymentHandler {
    private readonly entityManagerService = inject(EntityManagerService);
    private readonly genericListDialogService = inject(GenericListDialogService);
    private readonly saleUtilsService = inject(SaleUtilsService);
    private readonly translateService = inject(TranslateService);
    private readonly waitDialogService = inject(WaitDialogService);

    private sendTransactionRequest(apiUrl: string, transactionRequest: ZPayBridge.TransactionRequest): Promise<ZPayBridge.Transaction> {
        return fetch(`http://${apiUrl}/api/transaction/start`, {
            method: 'POST',
            body: JSON.stringify(transactionRequest),
            headers: {
                'Content-Type': 'application/json'
            }
        }).then(res => readJsonOrThrow(res));
    }

    private getPosAllowedMethods(pos: ZPayBridge.POS): ZPayBridge.SupportedMethods[] {
        if (!pos.supported_payments && !pos.supported_channels) {
            return [
                'auto',
                'ticket',
                'satispay'
            ];
        }

        const allowedMethods: Set<ZPayBridge.SupportedMethods> = new Set();

        if (pos.supported_payments) {
            for (const method of pos.supported_payments as ZPayBridge.SupportedMethods[]) {
                if (availableMethods.has(method)) {
                    allowedMethods.add(method);
                }
            }
        }

        if (pos.supported_channels) {
            for (const channel of pos.supported_channels as ZPayBridge.SupportedMethods[]) {
                if (availableMethods.has(channel)) {
                    allowedMethods.add(channel);
                }
            }
        }

        return Array.from(allowedMethods);
    }

    public getPOSList(apiUrl: string): Promise<ZPayBridge.POS[]> {
        return fetch(`http://${apiUrl}/api/pos`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        }).then(res => readJsonOrThrow(res));
    }

    /**
     * Given a detail voucher, parse it and extract the actual amount from it.
     * The detail voucher is a string with the following format:
     * <number of pairs>|<quantity1>|<value1>|<quantity2>|<value2>|...
     * The function will return the actual amount, divided by 100 and rounded to the nearest integer.
     * If the detail voucher is invalid, the function will return undefined.
     * @param detailVoucher The detail voucher to parse.
     * @returns The actual amount, or undefined if the detail voucher is invalid.
     */
    private parseDetailVoucher(detailVoucher: string): number | undefined {
        try {
            let actualAmount = 0;

            const detailVoucherArr = detailVoucher.split('|');
            const numPairs = Number(detailVoucherArr.shift());

            if (isNaN(numPairs) || numPairs < 0 || detailVoucherArr.length !== numPairs * 2) {
                throw new Error('Invalid detail voucher format.');
            }

            for (let i = 0; i < numPairs; i++) {
                const quantity = Number(detailVoucherArr.shift());
                const value = Number(detailVoucherArr.shift());

                if (isNaN(quantity) || isNaN(value)) {
                    throw new Error('Invalid quantity or value in detail voucher.');
                }

                actualAmount += quantity * value;
            }

            return MathUtils.round(actualAmount / 100) || undefined;
        } catch (error) {
            // Do nothing
            return undefined;
        }
    }

    public async payment(amount: number, options: DigitalPaymentHandlerOptions): Promise<DigitalPaymentHandlerResult[]> {
        const useTip = !!options.paymentMethod.configuration?.useTip;

        if(useTip) {
            //Try to obtain a tip sale item to check if tips are properly configured
            await this.saleUtilsService.getTipSaleItem(amount);
        }

        const paymentMethod = options.paymentMethod;
        const apiUrl = paymentMethod.schema_name!;

        // POS Selection
        const posList = await this.getPOSList(apiUrl);

        let selectedPos: ZPayBridge.POS | undefined;

        switch (posList.length) {
            case 0:
                throw 'DIGITAL_PAYMENTS.ZPAY_BRIDGE.NO_POS_AVAILABLE';
            case 1:
                selectedPos = posList[0];
                break;
            default:
                // Check if a category has a preferred POS
                const categories = await this.entityManagerService.categories.fetchCollectionOffline()
                    .then(categories => categories.filter(category => category.terminal_id));

                const categoriesInSale = new Set(options.sale.sale_items?.filter(saleItem => saleItem.category_id).map(saleItem => saleItem.category_id!) || []);

                const categoryWithPreferredPos = categories.find(category => categoriesInSale.has(category.id!) && category.terminal_id);

                if (categoryWithPreferredPos) {
                    selectedPos = posList.find(pos => pos.id === categoryWithPreferredPos.terminal_id);
                }

                if (!selectedPos) {
                    selectedPos = await this.genericListDialogService.openDialog({
                        data: {
                            title: { label: 'DIGITAL_PAYMENTS.ZPAY_BRIDGE.SELECT_POS' },
                            list: posList.map((pos) => ({
                                name: pos.name || pos.id || pos.device_type,
                                value: pos,
                                isSelected: false
                            }))
                        }
                    });
                }
                break;
        }

        if (!selectedPos) {
            throw 'CANCELED';
        }

        // Method Selection
        const methods = this.getPosAllowedMethods(selectedPos);

        if (!methods.length) {
            throw 'DIGITAL_PAYMENTS.ZPAY_BRIDGE.NO_METHODS_AVAILABLE';
        }

        let selectedMethod: ZPayBridge.SupportedMethods | undefined;

        if (options.sale.payments?.some(payment => payment.payment_method_type_id === 42 && payment.unclaimed)) {
            selectedMethod = 'auto';
        } else if (methods.length === 1) {
            selectedMethod = methods[0];
        }

        const availableMethodsArr = methods.map((type) => ({ name: `DIGITAL_PAYMENTS.ZPAY_BRIDGE.PAYMENT_TYPE.${type.toUpperCase()}`, value: type, isSelected: false }))

        if (!selectedMethod) {
            selectedMethod = await this.genericListDialogService.openDialog({
                data: {
                    title: { label: 'DIGITAL_PAYMENTS.ZPAY_BRIDGE.SELECT_PAYMENT_TYPE' },
                    list: availableMethodsArr
                }
            });
        }

        if (!selectedMethod) {
            throw 'CANCELED';
        }

        // Payment
        let transactionRequest: ZPayBridge.TransactionRequest;

        if (selectedMethod === 'satispay') {
            transactionRequest = {
                payment_type: 'alternative',
                channel_type: selectedMethod,
                type: 'payment',
                value: Math.round(amount * 100),
                pos_id: selectedPos.id,
                use_tip: useTip,
            };
        } else {
            transactionRequest = {
                payment_type: selectedMethod,
                type: 'payment',
                value: Math.round(amount * 100),
                pos_id: selectedPos.id,
                use_tip: useTip,
            }
        }

        const paymentPromise = this.sendTransactionRequest(apiUrl, transactionRequest);

        const response = await this.waitDialogService.openDialog({
            data: {
                message: 'DIGITAL_PAYMENTS.INGENICO_17.COMPLETE_ON_POS',
                promise: paymentPromise,
            }
        }) as ZPayBridge.Transaction;

        if (!response.success) {
            throw response.receipt || response.outcome_description || response.error || 'DIGITAL_PAYMENTS.ZPAY_BRIDGE.UNKNOWN_ERROR';
        }

        let actualAmount: number | undefined;

        if (selectedMethod === 'ticket' || selectedMethod === 'satispay') {
            //Parse detail voucher in order to get the amount of the used vouchers
            if (response.voucher_details?.vouchers) {
                actualAmount = MathUtils.round(response.voucher_details.vouchers.reduce((total, voucher) => total + voucher.denomination * voucher.quantity, 0) / 100);
            } else if (response.detail_voucher) {
                actualAmount = this.parseDetailVoucher(response.detail_voucher);
            }
        } else if(response.amount) {
            actualAmount = MathUtils.round(response.amount / 100);
        }

        const mainResult: DigitalPaymentHandlerResult = {
            amount: actualAmount,
            acquirer_id: (response.id_acquirer ? Number(response.id_acquirer) : undefined) || undefined,
            payment_data: JSON.stringify(response),
            acquirer_name: response.provider_name || undefined,
            unclaimed: false
        };

        if (selectedMethod === 'ticket') {
            mainResult.unclaimed = true;
        }

        const result = [mainResult];

        if(useTip && response.tip) {
            result.push({
                acquirer_id: (response.id_acquirer ? Number(response.id_acquirer) : undefined) || undefined,
                acquirer_name: response.provider_name || undefined,
                amount: MathUtils.round(response.tip / 100),
                is_tip: true,
                unclaimed: false,
            });
        }

        let methodName = availableMethodsArr.find(method => method.value === selectedMethod)?.name;

        if(methodName) {
            methodName = this.translateService.instant(methodName);

            for(const method of result) {
                method.payment_method_name = method.is_tip
                    ? `${methodName} mancia`
                    : methodName;
            }
        }

        return result;
    }

    getPaymentReceipt(paymentMethod: SalesPayments): string {
        try {
            const response = JSON.parse(paymentMethod.payment_data || '{}');

            return response.receipt || '';
        } catch (err) {
            return '';
        }
    }
}